import { MediaItem } from '../../../app/services/mediaItems';
import { normalizeNfc } from '../../../utils/text';
import { DEFAULT_FPS } from '../../../utils/timecode';
import {
  MaClip,
  MaCsvRow,
  MaLinkOptions,
  MaOptionChoice,
  MaOptionSlot,
  MaOptionSlotSource,
  MaSegment,
  MaSequence,
  MaVideoSpan,
  MaWarning,
  canonicalMediaKey,
  maTimeToSeconds,
  stripExtension,
  videoSpanIndexAt,
  videoSpansEqual,
} from './maTypes';

/**
 * パース済みシーケンス行（CSV / RPP 共通の MaCsvRow）をショット内の
 * メディアアイテムへリンクし、動画セグメント + オーディオクリップの
 * MaSequence を組み立てる。
 *
 * リンク優先順（大文字小文字を区別せず、Unicode は NFC 正規化して比較。
 * macOS の NFD ファイル名と RPP / CSV 内の NFC 表記の不一致を吸収する）:
 * 1. file_name 完全一致
 * 2. variation_name 完全一致
 * 3. file_name の拡張子除去一致
 * 4. variation_name の拡張子除去一致
 * 5. `_a` / `_b` 比較グループのベース名一致（アルファベット順先頭 = `_a` を採用）
 *
 * 同名テイクが複数ある場合は最新テイク（updated_at 降順の先頭）を使う。
 *
 * 動画行は同一動画で前行の end に隙間なく（0.05 秒以内）続く行を
 * 1 セグメントへ統合し、各行を本編の動画区間（baseVideoSpans）にする
 * （RPP のソースオフセット不連続な分割 / 保存 CSV の区間行のラウンドトリップ）。
 *
 * オーディオ行の帰属は開始位置パーティション方式:
 * シーケンスを動画の start 位置で区切り、`[動画start, 次の動画start)` に
 * 開始位置が入るオーディオがその動画セグメントに帰属する。
 * クリップは境界でカットせず end（未指定ならファイル末尾）まで再生する
 * （動画尺を超える分はテール再生）。
 *
 * トラック配置の重なりルール:
 * - track 明示指定: 同一トラックで重なる場合は後着優先。前のクリップを
 *   後続クリップの開始位置でカットする（truncateAtSeconds）
 * - track 未指定: 既存クリップと重ならない最初のトラックへ自動配置
 *   （名前付きレーンは空いていても使わない）。
 *   全トラックが埋まっていれば下に新規トラックを追加する。
 *   duration 未解析のクリップは終了位置不明のため開放端（無限長）として
 *   扱い、同一トラックへの誤った積み重ね（意図しないミックス）を防ぐ
 *
 * トラック名（文字列 track）はシーケンス全体で数値 ID へ採番する:
 * 明示数値の最大値 + 1 から行の初出順に割り当て、同名トラックは
 * セグメントを跨いで同じレーンになる（元の名前は MaClip.trackName に保持）。
 *
 * オプションスロット（`(x)` / `[x]`、optionSlotSources）:
 * スロットをオーディオ行と同じ規則でセグメントへ帰属させ、全候補を
 * メディア解決済みの MaClip テンプレートとして MaSequence.optionSlots に
 * 格納する。既定候補（初期選択、未指定は先頭）のクリップは帰属セグメントへ
 * 複製して通常再生・書き出しに乗せる（タイムライン編集で候補を切替できる）。
 *
 * ひとつ前の動画からの音の引き継ぎ（options.inheritFromPreviousSegment）:
 * 2 本目以降のセグメントで、自前の行（オーディオ行 / リンク失敗行 / スロット）の
 * 最初の開始位置を分岐点とし、それより前に始まる前セグメントのクリップと
 * スロットを同じ相対位置へ複製する（分岐点なし = 全て引き継ぐ）。
 * 分岐点をまたぐクリップは切らず、同一トラックの後着優先カットに任せる。
 * セグメント順に処理するため引き継ぎは連鎖する。
 */

/** Preview.tsx / MediaItemTable.tsx と同じ「ベース名_a」検出ルール */
const COMPARISON_SUFFIX_RE = /^(.+)_([a-zA-Z])$/;

interface VariationGroupLite {
  variationName: string;
  latestTake: MediaItem;
  allTakeIds: string[];
}

interface ResolvedMatch {
  group: VariationGroupLite;
  /** サイドバー非表示の対象（比較グループ一致時はベース配下の全グループ） */
  linkedGroups: VariationGroupLite[];
}

/**
 * セグメント内クリップのトラック配置・後着優先カットを解決する（クリップ配列を直接更新）。
 *
 * - 明示 track（> 0）: 同一トラックで重なる場合、前のクリップを後続の開始位置でカット
 * - track 未指定（0）: 既存クリップと重ならない最初のトラックへ自動配置。
 *   ただし名前付きレーン（RPP の Render Track List 等）はレーン自体に意味がある
 *   ため、空いていても自動配置では使わない（SE が「Music」行に表示されない）
 * - duration 未解析クリップは開放端（Infinity）扱い
 *
 * タイムライン編集モードでもクリップ変更のたびに再実行される。
 * 戻り値はクリップの最遅終了（clipsEndSeconds、カット後の実効値）。
 */
export function layoutSegmentClips(clips: MaClip[], warnings: MaWarning[]): number {
  // 再レイアウト時に前回のカットが残らないようリセット
  clips.forEach(c => {
    c.truncateAtSeconds = null;
  });

  // 開始位置昇順（同時開始は CSV 行順 = 後の行が「後着」）
  clips.sort((a, b) => a.startSeconds - b.startSeconds || a.line - b.line);

  /** 既知の終了位置。duration 未解析は開放端（Infinity）として扱う */
  const knownEnd = (c: MaClip) =>
    c.durationSeconds != null ? c.startSeconds + c.durationSeconds : Infinity;

  // --- 明示 track: 同一トラックで重なる場合は後着優先で前のクリップをカット ---
  const explicitByTrack = new Map<number, MaClip[]>();
  clips
    .filter(c => c.track > 0)
    .forEach(c => {
      const arr = explicitByTrack.get(c.track) || [];
      arr.push(c);
      explicitByTrack.set(c.track, arr);
    });

  explicitByTrack.forEach(trackClips => {
    for (let i = 0; i < trackClips.length - 1; i++) {
      const cur = trackClips[i];
      const next = trackClips[i + 1];
      if (knownEnd(cur) > next.startSeconds) {
        cur.truncateAtSeconds = next.startSeconds;
        if (next.startSeconds <= cur.startSeconds) {
          warnings.push({
            line: cur.line,
            message: `「${cur.audioItem.file_name}」は同一トラックの後続クリップと同時に開始するため再生されません`,
          });
        }
      }
    }
  });

  /** トラック占有区間の終端（カット後の実効値） */
  const effectiveEnd = (c: MaClip) => Math.min(knownEnd(c), c.truncateAtSeconds ?? Infinity);

  // トラック番号 → 使用区間リスト（明示指定クリップを先に登録）
  const intervals = new Map<number, Array<[number, number]>>();
  clips
    .filter(c => c.track > 0)
    .forEach(c => {
      const arr = intervals.get(c.track) || [];
      arr.push([c.startSeconds, effectiveEnd(c)]);
      intervals.set(c.track, arr);
    });

  // --- track 未指定: 上のトラックから順に、重ならない最初のトラックへ ---
  // 名前付きレーンは避ける（レーン名のラベルで自動配置クリップが表示されないように）
  const namedTracks = new Set(
    clips.filter(c => c.trackName != null && c.track > 0).map(c => c.track),
  );
  clips
    .filter(c => c.track === 0)
    .forEach(c => {
      const start = c.startSeconds;
      const end = effectiveEnd(c);
      let track = 1;
      for (;;) {
        if (!namedTracks.has(track)) {
          const arr = intervals.get(track) || [];
          const overlaps = arr.some(([s, e]) => start < e && end > s);
          if (!overlaps) {
            c.track = track;
            arr.push([start, end]);
            intervals.set(track, arr);
            break;
          }
        }
        track++;
      }
    });

  // クリップ終端の集計（カット後の実効長で算出。duration 未解析は 0 扱い）
  return clips.reduce(
    (max, c) =>
      Math.max(
        max,
        Math.min(c.startSeconds + (c.durationSeconds ?? 0), c.truncateAtSeconds ?? Infinity),
      ),
    0,
  );
}

/**
 * 本編動画を videoItem（`_a/_b` バリアント等）へ差し替えたときの
 * videoDisplaySeconds を解決する。
 *
 * - 明示 end（hasExplicitEnd）は行指定の表示尺を維持
 * - それ以外は差し替え先の実尺から再計算
 *   （最終 base 区間の開始 + (duration - 最終区間のファイル時刻)）
 * useMaTimelineEdit の本編動画バリアント切替で使用。
 * オプション候補の動画は丸ごと差し替えず videoSpans の区間になる
 * （resolveVideoSpans）ため、この関数は通さない。
 */
export function resolveSegmentVideo(
  segment: Pick<
    MaSegment,
    'videoItem' | 'baseVideoSpans' | 'videoDisplaySeconds' | 'hasExplicitEnd'
  >,
  videoItem: MediaItem,
): { videoItem: MediaItem; videoDisplaySeconds: number | null } {
  if (videoItem.id === segment.videoItem.id) {
    return { videoItem: segment.videoItem, videoDisplaySeconds: segment.videoDisplaySeconds };
  }
  const lastSpan = segment.baseVideoSpans[segment.baseVideoSpans.length - 1];
  const videoDisplaySeconds = segment.hasExplicitEnd
    ? segment.videoDisplaySeconds
    : videoItem.duration != null && videoItem.duration > 0
      ? lastSpan.startSeconds + Math.max(0, videoItem.duration - lastSpan.fileOffsetSeconds)
      : null;
  return { videoItem, videoDisplaySeconds };
}

/**
 * 同一区間とみなす開始位置の許容差（秒）。同じ位置の動画付きスロットは
 * 後勝ち（switchOrder の末尾優先）で 1 区間に畳む
 */
const VIDEO_SPAN_POSITION_EPSILON = 0.001;
/**
 * 前区間と「同一ファイルの通し再生」とみなすファイル時刻の許容差（秒。約 1.5 フレーム）。
 * 連続していれば区間を分けない（境界での不要なシークを避ける）
 */
const VIDEO_SPAN_CONTINUITY_EPSILON = 0.05;
/**
 * 同一動画の行を 1 セグメントへ統合する「前行の end との隙間」の許容差（秒）。
 * parseRppProject の分割アイテムグループ化（VIDEO_MERGE_GAP_EPSILON）と同じ値
 */
const VIDEO_ROW_MERGE_GAP_EPSILON = 0.05;
/**
 * 引き継ぎの分岐点と同時刻とみなす許容差（秒）。分岐点ちょうどに始まる
 * 前セグメントの音は「変化した部分」に属するため引き継がない
 */
const INHERIT_BRANCH_EPSILON = 1e-6;
/**
 * 本編の行とオプション既定候補のクリップを「同じ位置」とみなす許容差（秒）。
 * 同一メディアがこの範囲で重なると二重再生になるため警告する
 */
const DUPLICATE_CLIP_EPSILON = 0.001;
/**
 * 可変尺分岐: 選択候補と既定候補の動画コンテンツ尺の差がこれ（秒）を超える
 * スロットを「分岐点」とし、同一セグメント内でそれより後ろのスロット・
 * クリップ・表示尺を差分ぶんスライドする。REAPER 側の「後続の場面は
 * 既定候補の尺に合わせて置き、別候補を選んだら尺差だけ後ろへ動く」
 * 前提の配置（320_JBAD 等）を再現する
 */
const SLOT_LENGTH_SHIFT_EPSILON = 0.01;
/**
 * 可変尺分岐の発動条件: 次のスロットが「既定候補の終端」へ連結されていると
 * みなす許容差（秒。約 1.5 フレーム = VIDEO_SPAN_CONTINUITY_EPSILON と同値）。
 * 320_JBAD 型（場面パートを既定尺で隙間なく連結）だけをシフトし、
 * スロット位置を特定候補の動画内時刻に合わせた配置（140_TDCA 型。
 * 次スロットが既定候補の終端に一致しない）は従来どおり固定位置のままにする
 */
const SLOT_CHAIN_EPSILON = 0.05;

/**
 * 前区間と「同一ファイルの通し再生」になっている区間を畳む
 * （同一動画かつファイル時刻が連続。境界での不要なシークを避ける）
 */
function mergeContinuousVideoSpans(spans: MaVideoSpan[]): MaVideoSpan[] {
  const merged: MaVideoSpan[] = [];
  spans.forEach(span => {
    const prev = merged[merged.length - 1];
    if (
      prev &&
      prev.videoItem.id === span.videoItem.id &&
      Math.abs(
        prev.fileOffsetSeconds + (span.startSeconds - prev.startSeconds) - span.fileOffsetSeconds,
      ) <= VIDEO_SPAN_CONTINUITY_EPSILON
    ) {
      // 吸収する区間のコンテンツ尺は前区間起点に換算して引き継ぐ（表示尺延長用）
      if (span.contentDurationSeconds != null) {
        const extended = span.startSeconds - prev.startSeconds + span.contentDurationSeconds;
        prev.contentDurationSeconds = Math.max(prev.contentDurationSeconds ?? 0, extended);
      }
      return;
    }
    merged.push(span);
  });
  return merged;
}

/** 定義済みの値の最小値（すべて未定義なら undefined） */
function minDefined(...values: Array<number | undefined>): number | undefined {
  const defined = values.filter((v): v is number => v !== undefined);
  return defined.length > 0 ? Math.min(...defined) : undefined;
}

/**
 * 候補動画のファイルが次の区間境界より前に尽きる位置へ、本編の続き
 * （本編区間の同時刻のファイル時刻）の区間を挿入する。
 * 本編の表示尺（不明なら戻さない）の外では戻さない
 */
function insertBaseResumeSpans(params: {
  /** startSeconds 昇順の区間 */
  spans: MaVideoSpan[];
  /** 候補動画の区間 → ファイルの残り実尺（秒） */
  candidateFileRemaining: Map<MaVideoSpan, number>;
  baseSpans: MaVideoSpan[];
  baseDisplaySeconds: number | null;
}): MaVideoSpan[] {
  const { spans, candidateFileRemaining, baseSpans, baseDisplaySeconds } = params;
  const result: MaVideoSpan[] = [];
  spans.forEach((span, i) => {
    result.push(span);
    const fileRemaining = candidateFileRemaining.get(span);
    if (fileRemaining === undefined || baseDisplaySeconds == null) return;
    const resumeAt = span.startSeconds + fileRemaining;
    const nextStart = spans[i + 1]?.startSeconds ?? Infinity;
    if (
      resumeAt >= nextStart - VIDEO_SPAN_POSITION_EPSILON ||
      resumeAt >= baseDisplaySeconds - VIDEO_SPAN_POSITION_EPSILON
    ) {
      return;
    }
    const base = baseSpans[videoSpanIndexAt(baseSpans, resumeAt)];
    result.push({
      startSeconds: resumeAt,
      videoItem: base.videoItem,
      fileOffsetSeconds: base.fileOffsetSeconds + (resumeAt - base.startSeconds),
    });
  });
  return result;
}

/**
 * 候補適用後のセグメント表示尺（秒）を解決する。
 *
 * 最後の動画区間がオプション候補の区間（contentDurationSeconds を持つ）なら、
 * 本編の表示尺（videoDisplaySeconds）とコンテンツ終端の長い方を返す
 * （RPP のパレット上で最後まで配置した候補動画を切らずに再生する）。
 * 最後の区間が本編（候補なし / 候補動画が尽きて本編の続きへ戻った）なら
 * undefined（= 本編の表示尺のまま）
 */
export function resolveSegmentDisplaySeconds(
  videoDisplaySeconds: number | null,
  videoSpans: MaVideoSpan[],
): number | undefined {
  const last = videoSpans[videoSpans.length - 1];
  if (!last || last.contentDurationSeconds == null) return undefined;
  return Math.max(videoDisplaySeconds ?? 0, last.startSeconds + last.contentDurationSeconds);
}

/**
 * 選択中候補（既定含む）の動画から、セグメントの動画表示区間（videoSpans）を解決する。
 *
 * - スロットのない時間帯は本編の区間（baseSpans）のまま。本編が複数区間
 *   （RPP の不連続分割 / 保存 CSV の区間行）の場合、その切替点は候補適用後も残る
 * - 動画付き候補が選択中のスロットは、スロット位置から次の区間境界までを
 *   候補動画の区間にする。区間のファイル時刻はスロット位置 =
 *   候補の videoOffsetSeconds（RPP の SOFFS 由来）
 * - 候補動画のファイル（残り実尺）が次の区間境界より前に尽きる場合は、そこから
 *   本編の続き（本編区間の同時刻のファイル時刻）へ戻す。本編の表示尺の外では
 *   戻さない。表示尺の延長に使うコンテンツ尺はパレット配置の尺とファイルの
 *   残り実尺の短い方（ファイルより先へ延長しない）
 * - 複数スロットは位置順に区間が並ぶ。同一位置の競合のみ「最後に切り替えた
 *   スロットが勝つ」（未操作スロット（配列順）→ switchOrder 順に処理した後勝ち）
 * - 前区間と同一ファイルかつファイル時刻が通し再生と一致（誤差内）する区間は
 *   マージして区間を作らない
 */
export function resolveVideoSpans(params: {
  /** 本編行由来の動画区間（候補の影響なし・startSeconds 昇順・先頭は 0） */
  baseSpans: MaVideoSpan[];
  slots: MaOptionSlot[];
  selections: Record<string, string>;
  switchOrder?: string[];
  /** `_a/_b` バリアント切替の適用（本編・候補動画の両方に適用。既定: そのまま） */
  resolveItem?: (_item: MediaItem) => MediaItem;
  /** 本編の表示尺（秒。null / 未指定 = 不明）。候補動画が尽きた後に本編へ戻す範囲 */
  baseDisplaySeconds?: number | null;
}): MaVideoSpan[] {
  const {
    baseSpans,
    slots,
    selections,
    switchOrder = [],
    resolveItem = item => item,
    baseDisplaySeconds = null,
  } = params;

  const resolvedBaseSpans: MaVideoSpan[] = baseSpans.map(span => ({
    ...span,
    videoItem: resolveItem(span.videoItem),
  }));
  const spans: MaVideoSpan[] = resolvedBaseSpans.map(span => ({ ...span }));
  /** 候補動画の区間 → ファイルの残り実尺（秒。duration 未解析の候補は含めない） */
  const candidateFileRemaining = new Map<MaVideoSpan, number>();

  const orderedSlots = [
    ...slots.filter(slot => !switchOrder.includes(slot.key)),
    ...switchOrder
      .map(key => slots.find(slot => slot.key === key))
      .filter((slot): slot is MaOptionSlot => slot !== undefined),
  ];
  orderedSlots.forEach(slot => {
    const selectedKey = selections[slot.key];
    const choice =
      (selectedKey != null ? slot.choices.find(c => c.key === selectedKey) : undefined) ??
      slot.choices.find(c => c.key === slot.defaultChoiceKey) ??
      slot.choices[0];
    if (!choice?.videoItem) return;
    const videoItem = resolveItem(choice.videoItem);
    const fileOffsetSeconds = Math.max(0, choice.videoOffsetSeconds ?? 0);
    const fileRemainingSeconds =
      videoItem.duration != null && videoItem.duration > 0
        ? Math.max(0, videoItem.duration - fileOffsetSeconds)
        : undefined;
    const span: MaVideoSpan = {
      startSeconds: slot.startSeconds,
      videoItem,
      fileOffsetSeconds,
      contentDurationSeconds: minDefined(choice.videoDurationSeconds, fileRemainingSeconds),
    };
    if (fileRemainingSeconds !== undefined) candidateFileRemaining.set(span, fileRemainingSeconds);
    // 同一位置（先頭区間 0 を含む）は後勝ちで置き換え、それ以外は追加
    const existing = spans.findIndex(
      s => Math.abs(s.startSeconds - span.startSeconds) <= VIDEO_SPAN_POSITION_EPSILON,
    );
    if (existing >= 0) spans[existing] = span;
    else spans.push(span);
  });

  spans.sort((a, b) => a.startSeconds - b.startSeconds);

  const withResume = insertBaseResumeSpans({
    spans,
    candidateFileRemaining,
    baseSpans: resolvedBaseSpans,
    baseDisplaySeconds,
  });

  // 通し再生と一致する区間をマージ（同一ファイル + ファイル時刻が連続）
  return mergeContinuousVideoSpans(withResume);
}

/**
 * 可変尺分岐: 候補動画のコンテンツ尺（スロット位置起点の秒）。
 * パレット配置の尺とファイル残り実尺の短い方（videoSpans の
 * contentDurationSeconds と同じ規則）。動画なし候補・尺不明は undefined
 */
function choiceContentSeconds(choice: MaOptionChoice): number | undefined {
  if (!choice.videoItem) return undefined;
  const offset = Math.max(0, choice.videoOffsetSeconds ?? 0);
  const fileRemaining =
    choice.videoItem.duration != null && choice.videoItem.duration > 0
      ? Math.max(0, choice.videoItem.duration - offset)
      : undefined;
  return minDefined(choice.videoDurationSeconds, fileRemaining);
}

/** 可変尺分岐のシフト計画（resolveSlotShifts の結果） */
interface SlotShiftPlan {
  /** スロットキー → そのスロット自体の移動量（より前の分岐の累積差分。秒） */
  shiftBySlotKey: Map<string, number>;
  /** 分岐点（元の配置基準のスロット位置）と尺差。位置昇順 */
  breakpoints: Array<{ atSeconds: number; deltaSeconds: number }>;
  /** 全分岐の合計差分（セグメント表示尺の伸縮量。秒） */
  totalShiftSeconds: number;
}

/**
 * 可変尺分岐のシフト量を計算する。
 *
 * 選択候補と既定候補の両方が動画付きでコンテンツ尺が分かり、その差が
 * SLOT_LENGTH_SHIFT_EPSILON を超えるスロットを分岐点として、同一セグメント内で
 * それより後ろのスロット・クリップへ累積差分を適用する（複数分岐は合算）。
 * 動画なし候補・尺不明の候補は分岐点にしない（固定位置のまま = 従来挙動）
 */
function resolveSlotShifts(
  slots: MaOptionSlot[],
  selections: Record<string, string>,
): SlotShiftPlan {
  const breakpoints: Array<{ atSeconds: number; deltaSeconds: number }> = [];
  const shiftBySlotKey = new Map<string, number>();
  let cumulative = 0;
  const sorted = [...slots].sort((a, b) => a.startSeconds - b.startSeconds);
  sorted.forEach((slot, index) => {
    shiftBySlotKey.set(slot.key, cumulative);
    const selectedKey = selections[slot.key];
    if (selectedKey == null || selectedKey === slot.defaultChoiceKey) return;
    const selected = slot.choices.find(c => c.key === selectedKey);
    const defaultChoice =
      slot.choices.find(c => c.key === slot.defaultChoiceKey) ?? slot.choices[0];
    if (!selected || !defaultChoice) return;
    const selectedLen = choiceContentSeconds(selected);
    const defaultLen = choiceContentSeconds(defaultChoice);
    if (selectedLen == null || defaultLen == null) return;
    const delta = selectedLen - defaultLen;
    if (Math.abs(delta) <= SLOT_LENGTH_SHIFT_EPSILON) return;
    // 連結配置の判定: 次のスロットが既定候補の終端に一致するときだけシフトする。
    // 一致しない配置（スロット位置を特定候補の動画内時刻に合わせた 140_TDCA 型）で
    // シフトすると、従来ぴったり揃っていた後続候補がズレてしまうため
    const next = sorted
      .slice(index + 1)
      .find(s => s.startSeconds > slot.startSeconds + VIDEO_SPAN_POSITION_EPSILON);
    if (!next) return;
    const chainGap = Math.abs(next.startSeconds - (slot.startSeconds + defaultLen));
    if (chainGap > SLOT_CHAIN_EPSILON) return;
    breakpoints.push({ atSeconds: slot.startSeconds, deltaSeconds: delta });
    cumulative += delta;
  });
  return { shiftBySlotKey, breakpoints, totalShiftSeconds: cumulative };
}

/** 位置 t（元の配置基準・秒）に適用する累積シフト量（t より前の分岐の差分合計） */
function shiftAtSeconds(plan: SlotShiftPlan, t: number): number {
  return plan.breakpoints.reduce(
    (sum, bp) => (t > bp.atSeconds + VIDEO_SPAN_POSITION_EPSILON ? sum + bp.deltaSeconds : sum),
    0,
  );
}

/**
 * オプションスロットの候補選択をセグメントへ適用した表示・再生用セグメントを返す。
 *
 * タイムライン編集を経由しない「プレビュー専用の候補切替」
 * （通常 PREVIEW 画面の動画下チップ）と、編集モード開始時の初期状態構築で使う。
 * 候補の動画は videoSpans の区間として反映される（スロット位置から先だけが
 * 候補動画になり、ファイル時刻は候補の videoOffsetSeconds 起点。
 * 同一位置の競合のみ後勝ち = switchOrder の末尾優先）。
 * 選択が全スロットとも既定候補（または不正キー）で動画区間も変わらない場合は
 * 元の segment 参照をそのまま返す（不要な再レンダリング・再スケジュールを避ける）。
 *
 * 可変尺分岐（options.variableLengthShift・既定 ON）: 選択候補と既定候補の
 * 動画コンテンツ尺が異なるスロットを分岐点に、後続のスロット位置・クリップ・
 * 表示尺を尺差ぶんスライドする（既定選択時は差分 0 = 従来どおり）。
 * タイムライン編集モードは「候補のテンプレート配置が正」であり、保存 CSV に
 * シフト位置が焼き込まれないよう OFF にすること
 */
export function applyOptionSelectionsToSegment(
  segment: MaSegment,
  slots: MaOptionSlot[],
  selections: Record<string, string>,
  switchOrder: string[] = [],
  options: { variableLengthShift?: boolean } = {},
): MaSegment {
  const { variableLengthShift = true } = options;
  const changedSlots = slots.filter(slot => {
    const selectedKey = selections[slot.key];
    return (
      selectedKey != null &&
      selectedKey !== slot.defaultChoiceKey &&
      slot.choices.some(c => c.key === selectedKey)
    );
  });

  // ===== 可変尺分岐: 候補の尺差で後続スロット・クリップ・表示尺をスライド =====
  const shiftPlan: SlotShiftPlan = variableLengthShift
    ? resolveSlotShifts(slots, selections)
    : { shiftBySlotKey: new Map(), breakpoints: [], totalShiftSeconds: 0 };
  const hasShift = shiftPlan.breakpoints.length > 0;
  const shiftedSlots = hasShift
    ? slots.map(slot => {
        const shift = shiftPlan.shiftBySlotKey.get(slot.key) ?? 0;
        return shift !== 0 ? { ...slot, startSeconds: slot.startSeconds + shift } : slot;
      })
    : slots;
  const shiftedDisplaySeconds =
    hasShift && segment.videoDisplaySeconds != null
      ? Math.max(0, segment.videoDisplaySeconds + shiftPlan.totalShiftSeconds)
      : segment.videoDisplaySeconds;

  // 動画表示区間: 選択中候補の動画をスロット位置からの区間として解決
  const videoSpans = resolveVideoSpans({
    baseSpans: segment.baseVideoSpans,
    slots: shiftedSlots,
    selections,
    switchOrder,
    baseDisplaySeconds: shiftedDisplaySeconds,
  });
  const spansChanged = !videoSpansEqual(videoSpans, segment.videoSpans);
  const resolvedDisplaySeconds =
    resolveSegmentDisplaySeconds(shiftedDisplaySeconds, videoSpans) ??
    // 最後の区間が本編でも、シフトで本編の表示尺自体が伸縮している場合は反映する
    (hasShift ? (shiftedDisplaySeconds ?? undefined) : undefined);

  if (changedSlots.length === 0 && !spansChanged) return segment;
  if (changedSlots.length === 0) {
    // クリップは既定のまま動画区間だけ変わるケース（切替順による同一位置の勝者変更など）
    return { ...segment, videoItem: videoSpans[0].videoItem, videoSpans, resolvedDisplaySeconds };
  }

  const changedKeys = new Set(changedSlots.map(slot => slot.key));
  /** クリップの移動量: スロット所属はスロットの移動量、本編直置きは位置基準の累積差分 */
  const clipShiftSeconds = (clip: MaClip): number => {
    if (!hasShift) return 0;
    return clip.optionSlotKey != null
      ? (shiftPlan.shiftBySlotKey.get(clip.optionSlotKey) ?? 0)
      : shiftAtSeconds(shiftPlan, clip.startSeconds);
  };
  // layoutSegmentClips はクリップを直接更新するため、必ず複製してから渡す
  const clips: MaClip[] = segment.clips
    .filter(clip => !clip.optionSlotKey || !changedKeys.has(clip.optionSlotKey))
    .map(clip => {
      const shift = clipShiftSeconds(clip);
      return shift !== 0 ? { ...clip, startSeconds: clip.startSeconds + shift } : { ...clip };
    });
  changedSlots.forEach(slot => {
    const choice = slot.choices.find(c => c.key === selections[slot.key]);
    const shift = hasShift ? (shiftPlan.shiftBySlotKey.get(slot.key) ?? 0) : 0;
    choice?.clips.forEach(clip =>
      clips.push(shift !== 0 ? { ...clip, startSeconds: clip.startSeconds + shift } : { ...clip }),
    );
  });

  // プレビュー切替の警告（同時開始カット等）は UI 未表示のため破棄する
  const warnings: MaWarning[] = [];
  const clipsEndSeconds = layoutSegmentClips(clips, warnings);
  return {
    ...segment,
    videoItem: videoSpans[0].videoItem,
    videoSpans,
    resolvedDisplaySeconds,
    clips,
    clipsEndSeconds,
  };
}

/**
 * variation_name ごとにグループ化（最新テイク = updated_at 降順の先頭）。
 * NFD / NFC の表記ゆれは同じバリエーション（Preview.tsx のスライドと同じキー）
 */
function buildGroups(items: MediaItem[]): VariationGroupLite[] {
  const map = new Map<string, MediaItem[]>();
  items.forEach(item => {
    const key = normalizeNfc(item.variation_name);
    const arr = map.get(key) || [];
    arr.push(item);
    map.set(key, arr);
  });
  return Array.from(map.entries()).map(([variationName, takes]) => {
    const sorted = [...takes].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
    return {
      variationName,
      latestTake: sorted[0],
      allTakeIds: sorted.map(t => t.id),
    };
  });
}

/** メディア参照文字列 → グループのリゾルバを構築 */
function buildResolver(groups: VariationGroupLite[]) {
  const byFileName = new Map<string, VariationGroupLite>();
  const byName = new Map<string, VariationGroupLite>();
  const byFileStem = new Map<string, VariationGroupLite>();
  const byNameStem = new Map<string, VariationGroupLite>();
  /** ベース名（小文字） → サフィックス文字順のグループ一覧 */
  const byComparisonBase = new Map<string, { letter: string; group: VariationGroupLite }[]>();

  const setIfAbsent = (map: Map<string, VariationGroupLite>, key: string, group: VariationGroupLite) => {
    if (key && !map.has(key)) map.set(key, group);
  };

  groups.forEach(group => {
    const fileName = canonicalMediaKey(group.latestTake.file_name || '');
    const name = canonicalMediaKey(group.variationName);
    setIfAbsent(byFileName, fileName, group);
    setIfAbsent(byName, name, group);
    setIfAbsent(byFileStem, stripExtension(fileName), group);
    setIfAbsent(byNameStem, stripExtension(name), group);

    // 比較グループ候補の登録（variation_name / file_name 両方のステムから検出）
    const stems = new Set([stripExtension(name), stripExtension(fileName)]);
    stems.forEach(stem => {
      const m = stem.match(COMPARISON_SUFFIX_RE);
      if (!m) return;
      const base = m[1];
      const letter = m[2].toLowerCase();
      const arr = byComparisonBase.get(base) || [];
      if (!arr.some(e => e.group === group)) {
        arr.push({ letter, group });
        byComparisonBase.set(base, arr);
      }
    });
  });

  byComparisonBase.forEach(arr => arr.sort((a, b) => a.letter.localeCompare(b.letter)));

  return (mediaRef: string): ResolvedMatch | null => {
    const ref = canonicalMediaKey(mediaRef.trim());
    if (!ref) return null;

    const direct =
      byFileName.get(ref) ||
      byName.get(ref) ||
      byFileStem.get(stripExtension(ref)) ||
      byNameStem.get(stripExtension(ref));
    if (direct) return { group: direct, linkedGroups: [direct] };

    // 比較グループ: ベース名一致で先頭サフィックス（_a）を採用。
    // 「同じファイル」とみなすため、ベース配下の全グループをリンク済み扱いにする
    const comparison = byComparisonBase.get(stripExtension(ref));
    if (comparison && comparison.length > 0) {
      return {
        group: comparison[0].group,
        linkedGroups: comparison.map(e => e.group),
      };
    }
    return null;
  };
}

/**
 * 2 本目以降のセグメントへ、ひとつ前のセグメントで分岐点（branchSeconds[i]、
 * セグメント相対秒）より前に始まるクリップとオプションスロットを同じ相対位置で
 * 複製する（segments / optionSlots を直接更新）。セグメント順に処理するため、
 * 引き継いだ音はさらに次のセグメントへ連鎖する
 */
function inheritPreviousSegmentAudio(
  segments: MaSegment[],
  optionSlots: MaOptionSlot[],
  branchSeconds: number[],
): void {
  const usedSlotKeys = new Set(optionSlots.map(slot => slot.key));
  const beforeBranch = (startSeconds: number, branch: number) =>
    startSeconds < branch - INHERIT_BRANCH_EPSILON;

  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1];
    const segment = segments[i];
    const branch = branchSeconds[i];

    // スロット由来クリップはスロットごと複製するためここでは除外
    prev.clips
      .filter(clip => !clip.optionSlotKey && beforeBranch(clip.startSeconds, branch))
      .forEach(clip => segment.clips.push({ ...clip }));

    optionSlots
      .filter(slot => slot.segmentIndex === i - 1 && beforeBranch(slot.startSeconds, branch))
      .forEach(slot => {
        const baseKey = `${slot.name}@${segment.sequenceStartSeconds + slot.startSeconds}`;
        let key = baseKey;
        for (let n = 2; usedSlotKeys.has(key); n++) key = `${baseKey}~${n}`;
        usedSlotKeys.add(key);

        const choices = slot.choices.map(choice => ({
          ...choice,
          clips: choice.clips.map(clip => ({ ...clip, optionSlotKey: key })),
        }));
        const defaultChoice = choices.find(c => c.key === slot.defaultChoiceKey) ?? choices[0];
        defaultChoice.clips.forEach(clip => segment.clips.push({ ...clip }));
        optionSlots.push({ ...slot, key, segmentIndex: i, choices });
      });
  }

  // スロットはシーケンス上の位置順（パース結果と同じ並び）に保つ
  optionSlots.sort((a, b) => a.segmentIndex - b.segmentIndex || a.startSeconds - b.startSeconds);
}

export function linkMaSequence(
  rows: MaCsvRow[],
  items: MediaItem[],
  optionSlotSources: MaOptionSlotSource[] = [],
  options: Partial<MaLinkOptions> = {},
): MaSequence {
  const { inheritFromPreviousSegment = false, rulerLanes = [], videoTrackName = null } = options;
  const warnings: MaWarning[] = [];
  const linkedItemIds = new Set<string>();
  const groups = buildGroups(items);
  const resolve = buildResolver(groups);

  // レーン色のフォールバック（個別色のない行 → トラック名一致のレーン色）
  const laneColorByName = new Map<string, string>();
  rulerLanes.forEach(lane => {
    const key = canonicalMediaKey(lane.name);
    if (lane.colorHex && !laneColorByName.has(key)) laneColorByName.set(key, lane.colorHex);
  });
  const resolveClipColor = (row: Pick<MaCsvRow, 'color' | 'track'>): string | null => {
    if (row.color) return row.color;
    if (typeof row.track === 'string') {
      return laneColorByName.get(canonicalMediaKey(row.track)) ?? null;
    }
    return null;
  };

  // ===== 1. 行のメディア解決 =====
  interface ResolvedRow {
    row: MaCsvRow;
    item: MediaItem;
  }
  const videoRows: ResolvedRow[] = [];
  const audioRows: ResolvedRow[] = [];
  /** メディア未解決の行（引き継ぎの分岐点の目印としてのみ使う） */
  const unresolvedRows: MaCsvRow[] = [];

  rows.forEach(row => {
    const match = resolve(row.media);
    if (!match) {
      unresolvedRows.push(row);
      warnings.push({
        line: row.line,
        message: `「${row.media}」に一致するメディアアイテムが見つかりません（行をスキップ）`,
      });
      return;
    }
    const item = match.group.latestTake;
    if (item.file_type === 'video') {
      videoRows.push({ row, item });
    } else if (item.file_type === 'audio') {
      audioRows.push({ row, item });
    } else {
      warnings.push({
        line: row.line,
        message: `「${row.media}」は動画 / オーディオではないためスキップしました（${item.file_type}）`,
      });
      return;
    }
    match.linkedGroups.forEach(g => g.allTakeIds.forEach(id => linkedItemIds.add(id)));
  });

  if (videoRows.length === 0) {
    warnings.push({ line: null, message: 'シーケンスに動画の行がないため、MA プレビューは無効です' });
    return {
      fps: DEFAULT_FPS,
      segments: [],
      warnings,
      linkedItemIds: new Set(),
      optionSlots: [],
      displayNames: new Map(),
      rulerLanes,
      videoTrackName,
    };
  }

  // ===== 2. シーケンス fps の決定（最初の動画の frame_rate、未解析は DEFAULT_FPS） =====
  const firstVideo = videoRows[0].item;
  const fps = firstVideo.frame_rate && firstVideo.frame_rate > 0 ? firstVideo.frame_rate : DEFAULT_FPS;
  videoRows.forEach(({ row, item }) => {
    if (item.frame_rate && item.frame_rate > 0 && Math.abs(item.frame_rate - fps) > 0.01) {
      warnings.push({
        line: row.line,
        message: `動画の fps が混在しています（${item.frame_rate}fps）。フレーム換算はシーケンス fps=${fps} で統一されます`,
      });
    }
  });

  // ===== 3. 動画セグメント構築（start 昇順） =====
  // 同一動画で前行の end に隙間なく続く行（RPP の不連続分割 / 保存 CSV の
  // 区間行）は 1 セグメントへ統合し、各行を本編の動画区間（baseVideoSpans）に
  // する。動画 1 本の分割が複数スライドにならず、ジャンプカット / リピート
  // 配置も各行のソースオフセット（ファイル時刻）どおりに再生される
  interface ResolvedVideoRow {
    row: MaCsvRow;
    item: MediaItem;
    start: number;
    /** 有効な明示 end（end <= start は警告して無効化） */
    end: number | null;
    offset: number;
  }
  const resolvedVideoRows: ResolvedVideoRow[] = videoRows.map(({ row, item }) => {
    const start = maTimeToSeconds(row.start, fps);
    const offset = row.offset ? maTimeToSeconds(row.offset, fps) : 0;
    let end = row.end ? maTimeToSeconds(row.end, fps) : null;
    if (end !== null && end <= start) {
      warnings.push({ line: row.line, message: 'end が start 以前のため end を無視しました' });
      end = null;
    }
    return { row, item, start, end, offset };
  });
  resolvedVideoRows.sort((a, b) => a.start - b.start || a.row.line - b.row.line);

  const videoRuns: ResolvedVideoRow[][] = [];
  resolvedVideoRows.forEach(resolved => {
    const run = videoRuns[videoRuns.length - 1];
    const prev = run?.[run.length - 1];
    const adjacent =
      prev !== undefined &&
      prev.item.id === resolved.item.id &&
      prev.end !== null &&
      Math.abs(resolved.start - prev.end) <= VIDEO_ROW_MERGE_GAP_EPSILON;
    if (adjacent) run.push(resolved);
    else videoRuns.push([resolved]);
  });

  const segments: MaSegment[] = videoRuns.map(run => {
    const first = run[0];
    const last = run[run.length - 1];
    const item = first.item;

    const baseVideoSpans = mergeContinuousVideoSpans(
      run.map(r => ({
        startSeconds: r.start - first.start,
        videoItem: r.item,
        fileOffsetSeconds: r.offset,
      })),
    );

    let videoDisplaySeconds: number | null = null;
    let hasExplicitEnd = false;
    if (last.end !== null) {
      videoDisplaySeconds = last.end - first.start;
      hasExplicitEnd = true;
    } else if (item.duration != null && item.duration > 0) {
      // 末尾行に end がない場合は最終区間を実尺まで再生
      videoDisplaySeconds = last.start - first.start + Math.max(0, item.duration - last.offset);
    }

    return {
      sequenceStartSeconds: first.start,
      videoItem: item,
      baseVideoItem: item,
      baseVideoSpans,
      videoSpans: baseVideoSpans,
      videoDisplaySeconds,
      hasExplicitEnd,
      clips: [],
      clipsEndSeconds: 0,
      line: first.row.line,
    } as MaSegment;
  });

  /** 開始位置が [seg.start, 次 seg.start) に入るセグメント index（最初の動画より前は -1） */
  const findSegmentIndex = (absSeconds: number): number => {
    for (let i = 0; i < segments.length; i++) {
      const nextStart = segments[i + 1]?.sequenceStartSeconds ?? Infinity;
      if (absSeconds >= segments[i].sequenceStartSeconds && absSeconds < nextStart) return i;
    }
    return -1;
  };

  // ===== 4. トラック名の採番（明示数値の最大値 + 1 から、行の初出順） =====
  // ルーラーレーンがあればレーン index 順に事前採番し、タイムラインの
  // トラック順を RPP のレーン順に揃える（行に登場しないレーンは
  // ID を消費するだけでトラックは作られない）
  const trackIdByName = new Map<string, number>();
  let nextNamedTrackId =
    audioRows.reduce(
      (max, { row }) => (typeof row.track === 'number' ? Math.max(max, row.track) : max),
      0,
    ) + 1;
  [...rulerLanes]
    .sort((a, b) => a.index - b.index)
    .forEach(lane => {
      if (trackIdByName.has(lane.name)) return;
      trackIdByName.set(lane.name, nextNamedTrackId++);
    });
  const resolveTrack = (
    rawTrack: number | string | null,
  ): { track: number; trackName: string | null } => {
    if (typeof rawTrack === 'number') return { track: rawTrack, trackName: null };
    if (rawTrack === null) return { track: 0, trackName: null }; // 0 = 自動配置
    let id = trackIdByName.get(rawTrack);
    if (id === undefined) {
      id = nextNamedTrackId++;
      trackIdByName.set(rawTrack, id);
    }
    return { track: id, trackName: rawTrack };
  };

  // ===== 5. オーディオ行の帰属（開始位置パーティション） =====
  audioRows.forEach(({ row, item }) => {
    const startAbs = maTimeToSeconds(row.start, fps);
    const segIndex = findSegmentIndex(startAbs);
    if (segIndex === -1) {
      warnings.push({
        line: row.line,
        message: `「${row.media}」は最初の動画より前に開始するためスキップしました`,
      });
      return;
    }

    const segment = segments[segIndex];
    const relStart = startAbs - segment.sequenceStartSeconds;

    const endAbs = row.end ? maTimeToSeconds(row.end, fps) : null;
    let durationSeconds: number | null = null;
    let hasExplicitEnd = false;
    if (endAbs !== null) {
      if (endAbs <= startAbs) {
        warnings.push({ line: row.line, message: 'end が start 以前のため end を無視しました' });
        durationSeconds = item.duration != null && item.duration > 0 ? item.duration : null;
      } else {
        durationSeconds = endAbs - startAbs;
        hasExplicitEnd = true;
      }
    } else if (item.duration != null && item.duration > 0) {
      durationSeconds = item.duration;
    }

    const { track, trackName } = resolveTrack(row.track);
    const clip: MaClip = {
      audioItem: item,
      sourceName: row.media,
      startSeconds: relStart,
      durationSeconds,
      hasExplicitEnd,
      truncateAtSeconds: null, // 後段の同一トラック重なり解決で設定
      track, // 0 = 自動配置（後段で解決）
      trackName,
      gainDb: row.gainDb ?? 0,
      line: row.line,
      color: resolveClipColor(row),
    };
    segment.clips.push(clip);
  });

  // ===== 5.5. オプションスロット（(x) / [x]）のリンクと既定候補の配置 =====
  // 全候補をここでクリップ化しておく（編集時の候補切替で再リンク不要にするため）。
  // 既定候補（先頭）のクリップは帰属セグメントへ複製して通常再生に乗せる
  const optionSlots: MaOptionSlot[] = [];
  // リンクできたスロットの配置（巻き戻り検出用。引き継ぎによる複製は含めない）
  const linkedSlotPlacements: Array<{
    name: string;
    line: number;
    segmentIndex: number;
    absSeconds: number;
    hasVideoChoice: boolean;
  }> = [];
  optionSlotSources.forEach(source => {
    // スロットの帰属セグメント（オーディオ行と同じ開始位置パーティション）
    const slotAbs = maTimeToSeconds(source.start, fps);
    const segIndex = findSegmentIndex(slotAbs);
    if (segIndex === -1) {
      warnings.push({
        line: source.line,
        message: `オプション「(${source.name})」は最初の動画より前に開始するためスキップしました`,
      });
      return;
    }
    const segment = segments[segIndex];
    const slotRelStart = slotAbs - segment.sequenceStartSeconds;

    const choices: MaOptionChoice[] = [];
    source.choices.forEach(choiceSource => {
      const clips: MaClip[] = [];
      let choiceVideo: MediaItem | null = null;
      let choiceVideoOffset = 0;
      let choiceVideoDuration: number | undefined;
      choiceSource.rows.forEach(row => {
        const match = resolve(row.media);
        if (!match) {
          warnings.push({
            line: row.line,
            message: `オプション候補「${row.media}」に一致するメディアアイテムが見つかりません（行をスキップ）`,
          });
          return;
        }
        const item = match.group.latestTake;
        // 動画行 = 候補の差し替え動画（パレット領域の動画アイテム由来）。
        // SOFFS（row.offset）はスロット位置のファイル時刻として保持する。
        // クラスタ先頭より後ろに置かれた動画はスロット位置へ正規化
        // （スロット時点のファイル時刻 = SOFFS - クラスタ内相対開始）し、
        // REAPER で Region の下に見えるフレームとの対応を保つ
        if (item.file_type === 'video') {
          if (choiceVideo) {
            warnings.push({
              line: row.line,
              message: `オプション候補「${choiceSource.label}」に動画が複数あります（先頭のみ使用）`,
            });
            return;
          }
          choiceVideo = item;
          const videoRelStart = maTimeToSeconds(row.start, fps);
          const videoRelEnd = row.end ? maTimeToSeconds(row.end, fps) : null;
          const videoSourceOffset = row.offset ? maTimeToSeconds(row.offset, fps) : 0;
          choiceVideoOffset = Math.max(0, videoSourceOffset - videoRelStart);
          // 候補動画のコンテンツ尺（スロット起点）: パレット配置の end（クラスタ
          // 相対）をそのまま使う。end がなければメディア実尺から算出
          // （クラスタ内相対開始 + SOFFS 以降の残り実尺）。
          // 本編より長い候補を選んだときの表示尺延長（resolvedDisplaySeconds）に使う
          choiceVideoDuration =
            videoRelEnd !== null && videoRelEnd > videoRelStart
              ? videoRelEnd
              : item.duration != null && item.duration > 0
                ? videoRelStart + Math.max(0, item.duration - videoSourceOffset)
                : undefined;
          match.linkedGroups.forEach(g => g.allTakeIds.forEach(id => linkedItemIds.add(id)));
          return;
        }
        if (item.file_type !== 'audio') {
          warnings.push({
            line: row.line,
            message: `オプション候補「${row.media}」はオーディオではないためスキップしました（${item.file_type}）`,
          });
          return;
        }
        match.linkedGroups.forEach(g => g.allTakeIds.forEach(id => linkedItemIds.add(id)));

        // 候補行の時刻はクラスタ先頭を 0 とする相対値（RPP 由来は秒、CSV 由来はフレーム）
        const relStart = maTimeToSeconds(row.start, fps);
        const relEnd = row.end ? maTimeToSeconds(row.end, fps) : null;
        let durationSeconds: number | null = null;
        let hasExplicitEnd = false;
        if (relEnd !== null && relEnd > relStart) {
          durationSeconds = relEnd - relStart;
          hasExplicitEnd = true;
        } else if (item.duration != null && item.duration > 0) {
          durationSeconds = item.duration;
        }
        const { track, trackName } = resolveTrack(row.track);
        clips.push({
          audioItem: item,
          sourceName: row.media,
          startSeconds: slotRelStart + relStart,
          durationSeconds,
          hasExplicitEnd,
          truncateAtSeconds: null,
          track,
          trackName,
          gainDb: row.gainDb ?? 0,
          line: row.line,
          optionSlotKey: source.key,
          color: resolveClipColor(row),
        });
      });
      // 動画のみの候補も許容する（選択で表示動画だけが差し替わる）
      if (clips.length > 0 || choiceVideo) {
        choices.push({
          key: choiceSource.key,
          label: choiceSource.label,
          clips,
          videoItem: choiceVideo,
          videoOffsetSeconds: choiceVideo ? choiceVideoOffset : undefined,
          videoDurationSeconds: choiceVideo ? choiceVideoDuration : undefined,
        });
      }
    });

    if (choices.length === 0) {
      warnings.push({
        line: source.line,
        message: `オプション「(${source.name})」に再生できる候補がありません`,
      });
      return;
    }

    // 既定候補をセグメントへ配置（切替時はこのクリップ群が差し替えられる）
    const defaultChoice = choices.find(c => c.key === source.defaultChoiceKey) ?? choices[0];
    defaultChoice.clips.forEach(clip => segment.clips.push({ ...clip }));

    // 本編の行（オプション由来でないクリップ）と既定候補が同じメディア・同じ位置で
    // 重なっていると二重に鳴るため警告する（例: 本編のポイントマーカー行と
    // 同名候補のリージョン行を両方置いてしまったケース）
    defaultChoice.clips.forEach(clip => {
      const duplicated = segment.clips.find(
        base =>
          !base.optionSlotKey &&
          base.audioItem.id === clip.audioItem.id &&
          Math.abs(base.startSeconds - clip.startSeconds) <= DUPLICATE_CLIP_EPSILON,
      );
      if (!duplicated) return;
      warnings.push({
        line: duplicated.line,
        message: `「${duplicated.sourceName}」は本編の行とオプション「(${source.name})」の既定候補が同じ位置に重なっています（二重に鳴ります）`,
      });
    });

    optionSlots.push({
      key: source.key,
      name: source.name,
      segmentIndex: segIndex,
      startSeconds: slotRelStart,
      choices,
      defaultChoiceKey: defaultChoice.key,
    });
    linkedSlotPlacements.push({
      name: source.name,
      line: source.line,
      segmentIndex: segIndex,
      absSeconds: slotAbs,
      hasVideoChoice: choices.some(choice => choice.videoItem != null),
    });
  });

  // 動画付き候補を持つ同名セクションが「同一セグメント内の」位置の違う複数スロット
  // から参照されている場合、候補動画はファイル内時刻（SOFFS）が固定のため
  // 2 か所目以降は同じ場面へ巻き戻って見える。位置ごとに別セクションへ分けるよう
  // 警告する。別セグメントでの再利用（ゾーン分割の定型。各ゾーンが同じ候補群を
  // それぞれの位置で使う）は意図した配置なので対象外
  const videoSlotPlacementsBySegment = new Map<
    string,
    { name: string; line: number; positions: Set<number> }
  >();
  linkedSlotPlacements.forEach(placement => {
    if (!placement.hasVideoChoice) return;
    const key = `${placement.segmentIndex}:${placement.name}`;
    const positionKey = Math.round(placement.absSeconds * 1000);
    const entry = videoSlotPlacementsBySegment.get(key);
    if (entry) {
      entry.line = Math.min(entry.line, placement.line);
      entry.positions.add(positionKey);
    } else {
      videoSlotPlacementsBySegment.set(key, {
        name: placement.name,
        line: placement.line,
        positions: new Set([positionKey]),
      });
    }
  });
  videoSlotPlacementsBySegment.forEach(({ name, line, positions }) => {
    if (positions.size < 2) return;
    warnings.push({
      line,
      message: `オプション「(${name})」の動画付き候補が位置の違う ${positions.size} か所で使われています（候補動画はファイル内時刻が固定のため、2 か所目以降は同じ場面へ巻き戻って見えます。位置ごとに映像を変えるにはセクションを分けてください）`,
    });
  });

  // ===== 5.6. ひとつ前の動画からの音の引き継ぎ（分岐点より前） =====
  if (inheritFromPreviousSegment && segments.length > 1) {
    // 分岐点 = セグメント自前の行の最初の相対開始（複製前に確定させる）
    const branchSeconds = segments.map(() => Infinity);
    const markBranch = (absSeconds: number) => {
      const i = findSegmentIndex(absSeconds);
      if (i === -1) return;
      const rel = absSeconds - segments[i].sequenceStartSeconds;
      branchSeconds[i] = Math.min(branchSeconds[i], rel);
    };
    audioRows.forEach(({ row }) => markBranch(maTimeToSeconds(row.start, fps)));
    unresolvedRows.forEach(row => markBranch(maTimeToSeconds(row.start, fps)));
    optionSlots.forEach(slot => {
      branchSeconds[slot.segmentIndex] = Math.min(
        branchSeconds[slot.segmentIndex],
        slot.startSeconds,
      );
    });
    inheritPreviousSegmentAudio(segments, optionSlots, branchSeconds);
  }

  // ===== 5.7. 動画表示区間（videoSpans）の解決 =====
  // 既定候補の動画をスロット位置からの区間として反映する
  //（候補切替時は applyOptionSelectionsToSegment が同じ規則で再解決する）
  segments.forEach((segment, segIndex) => {
    const slots = optionSlots.filter(slot => slot.segmentIndex === segIndex);
    if (slots.length === 0) return;
    const videoSpans = resolveVideoSpans({
      baseSpans: segment.baseVideoSpans,
      slots,
      selections: {},
      baseDisplaySeconds: segment.videoDisplaySeconds,
    });
    segment.videoSpans = videoSpans;
    segment.videoItem = videoSpans[0].videoItem;
    // 既定候補の動画が本編より長い場合は表示尺を候補終端まで延長
    segment.resolvedDisplaySeconds = resolveSegmentDisplaySeconds(
      segment.videoDisplaySeconds,
      videoSpans,
    );
  });

  // ===== 6. トラック配置（明示: 後着優先カット / 自動: 重なったら下へ追加） =====
  segments.forEach(segment => {
    segment.clipsEndSeconds = layoutSegmentClips(segment.clips, warnings);
  });

  return {
    fps,
    segments,
    warnings,
    linkedItemIds,
    optionSlots,
    displayNames: new Map(),
    rulerLanes,
    videoTrackName,
  };
}
